import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import Polygon from '@arcgis/core/geometry/Polygon';
import { colord } from 'colord';
import { symbolToPictureMarkerMap } from './uscgSymbology';
import {
  isFillSymbol,
  isSimpleLineSymbol,
  isSimpleMarkerSymbol,
  isUscgSymbol,
} from './uscgSymbology';
import { createSimpleMarkerSymbol } from './simpleMarkerSymbol';
import {
  createPolylineSymbol,
  createPolylineFromPoints,
} from './polylineGraphics';
import {
  BOOM_RESOURCES,
  OIL_SPILL_RESOURCE,
  type IcsIncidentLocation,
} from 'hooks/icsLocation/icsIncidentLocation.types';
import type { InternalLayerVersionWithFeatures } from 'hooks/internalLayerVersion/useListInternalLayerVersionFeaturesQuery';
import { normalizeOpacity } from './normalizeOpacity';
import { WGS84_SPATIAL_REFERENCE } from './spatialReference';
import {
  locationStoredFields,
  storedFieldAttributes,
  storedFieldsPopupTemplate,
} from './locationPopup';

const DEFAULT_POLYGON_FILL = [30, 64, 175, 0.25];
const DEFAULT_POLYGON_OUTLINE = [30, 58, 138, 1];

function featurePopup(feature: IcsIncidentLocation) {
  return storedFieldsPopupTemplate(feature.name, locationStoredFields(feature));
}

function featureAttributes(
  feature: IcsIncidentLocation,
  incidentName?: string,
  extra?: Record<string, unknown>
) {
  return {
    id: feature.id,
    name: feature.name,
    incidentName,
    ...storedFieldAttributes(locationStoredFields(feature)),
    ...extra,
  };
}

/** Helper to transform a single feature into one or more Esri Graphics */
function createGraphicsForFeature(
  feature: IcsIncidentLocation,
  incidentName?: string
): Graphic[] {
  const { resourceType, properties } = feature;
  const graphics: Graphic[] = [];

  if (isUscgSymbol(resourceType) && isFillSymbol(resourceType)) {
    const geometry = feature.geometry;
    if (geometry.type === 'Polygon') {
      const polygonColor =
        'polygonColor' in properties ? properties.polygonColor : undefined;
      const polygonOpacity =
        'polygonOpacity' in properties ? properties.polygonOpacity : undefined;
      const parsedColor = polygonColor ? colord(polygonColor) : null;
      const rgb = parsedColor?.isValid() ? parsedColor.toRgb() : null;
      const fillOpacity = normalizeOpacity(polygonOpacity);
      const fillColor = rgb
        ? [rgb.r, rgb.g, rgb.b, fillOpacity]
        : DEFAULT_POLYGON_FILL;
      const outlineColor = rgb
        ? [rgb.r, rgb.g, rgb.b, 1]
        : DEFAULT_POLYGON_OUTLINE;

      graphics.push(
        new Graphic({
          geometry: new Polygon({
            rings: geometry.coordinates,
            spatialReference: WGS84_SPATIAL_REFERENCE,
          }),
          symbol: {
            type: 'simple-fill',
            color: fillColor,
            outline: {
              color: outlineColor,
              width: 2,
            },
          },
          attributes: featureAttributes(feature, incidentName),
          popupTemplate: featurePopup(feature),
        })
      );
    }
    return graphics;
  }

  if (
    isUscgSymbol(resourceType) &&
    isSimpleLineSymbol(resourceType) &&
    feature.geometry.type === 'LineString'
  ) {
    const points = feature.geometry.coordinates.map(
      ([longitude, latitude], idx) => ({
        longitude,
        latitude,
        order: idx,
      })
    );
    if (points.length >= 2) {
      graphics.push(
        new Graphic({
          geometry: createPolylineFromPoints(points),
          symbol: createPolylineSymbol(resourceType),
          attributes: featureAttributes(feature, incidentName),
          popupTemplate: featurePopup(feature),
        })
      );
    }
    return graphics;
  }

  if (
    isUscgSymbol(resourceType) &&
    isSimpleMarkerSymbol(resourceType) &&
    feature.geometry.type === 'Point'
  ) {
    const [longitude, latitude] = feature.geometry.coordinates;
    const pointColor =
      'pointColor' in properties ? properties.pointColor : undefined;
    const pointOpacity =
      'pointOpacity' in properties ? properties.pointOpacity : undefined;
    graphics.push(
      new Graphic({
        geometry: new Point({ longitude, latitude }),
        symbol: createSimpleMarkerSymbol(
          resourceType,
          typeof pointColor === 'string' ? pointColor : undefined,
          typeof pointOpacity === 'number' ? pointOpacity : 1
        ),
        attributes: featureAttributes(feature, incidentName),
        popupTemplate: featurePopup(feature),
      })
    );
    return graphics;
  }

  // 1. Resolve icon symbol
  const symbolKey = resourceType;
  const isBoom =
    symbolKey === BOOM_RESOURCES.PROPOSED ||
    symbolKey === BOOM_RESOURCES.COMPLETED;
  const isOilSpill = symbolKey === OIL_SPILL_RESOURCE;
  const iconSymbol = isUscgSymbol(symbolKey)
    ? symbolToPictureMarkerMap[symbolKey]
    : undefined;
  if (!iconSymbol) {
    console.error('Feature not supported', symbolKey, feature);
    return [];
  }

  // 2. Handle Multi-point Geometries (Booms & Oil Spills)
  if (feature.geometry.type === 'LineString') {
    const points = feature.geometry.coordinates.map(
      ([longitude, latitude], idx) => ({
        longitude,
        latitude,
        order: idx,
      })
    );
    if (points.length === 0) return [];

    // Completed booms render a line; proposed booms only render markers
    if (isBoom && symbolKey === BOOM_RESOURCES.COMPLETED) {
      graphics.push(
        new Graphic({
          geometry: createPolylineFromPoints(points),
          symbol: createPolylineSymbol('proposedBoom'),
          attributes: featureAttributes(feature, incidentName),
          popupTemplate: featurePopup(feature),
        })
      );
    }

    // Create a line connecting all points in order for oil spills
    if (isOilSpill && points.length >= 2) {
      graphics.push(
        new Graphic({
          geometry: createPolylineFromPoints(points),
          symbol: createPolylineSymbol('oilSpill'),
          attributes: featureAttributes(feature, incidentName),
          popupTemplate: featurePopup(feature),
        })
      );
    }

    // Booms also get a marker at every vertex/point
    if (isBoom && iconSymbol) {
      points.forEach((point) => {
        graphics.push(
          new Graphic({
            geometry: new Point({
              longitude: point.longitude,
              latitude: point.latitude,
            }),
            symbol: iconSymbol,
            attributes: featureAttributes(feature, incidentName, {
              pointOrder: point.order,
            }),
            popupTemplate: featurePopup(feature),
          })
        );
      });
    }

    return graphics;
  }

  // 3. Handle Standard Single Point Geometries
  if (feature.geometry.type === 'Point' && iconSymbol) {
    const [longitude, latitude] = feature.geometry.coordinates;
    graphics.push(
      new Graphic({
        geometry: new Point({ longitude, latitude }),
        symbol: iconSymbol,
        attributes: featureAttributes(feature, incidentName),
        popupTemplate: featurePopup(feature),
      })
    );
  }

  return graphics;
}

interface SyncParams {
  map: __esri.Map;
  internalLayerVersionsMap: Map<string, InternalLayerVersionWithFeatures>;
  internalLayerVisibilityById: Map<string, boolean | null>;
  layersRef: React.MutableRefObject<Map<string, GraphicsLayer>>;
}

/** Syncs internal layer version features to GraphicsLayer(s) on the map. */
export function syncInternalLayers({
  map,
  internalLayerVersionsMap,
  internalLayerVisibilityById,
  layersRef,
}: SyncParams) {
  // Update or Create Layers
  for (const [versionId, entry] of internalLayerVersionsMap) {
    const layerId = entry.internalLayer.id;
    const isVisible = internalLayerVisibilityById.get(layerId) ?? false;
    const features = entry.featuresQuery.data ?? [];

    let layer = layersRef.current.get(versionId);

    if (!layer) {
      layer = new GraphicsLayer({
        id: `internal-version-${versionId}`,
        title: `Internal Layer: ${versionId}`,
      });
      map.add(layer);
      layersRef.current.set(versionId, layer);
    }

    // Update Visibility
    const visibilityChanged = layer.visible !== isVisible;
    layer.visible = isVisible;

    // rymi-TODO: still flickering, if only visibility changed and data count is identical, don't redraw
    if (
      visibilityChanged &&
      layer.graphics.length > 0 &&
      layer.graphics.length === features.length
    ) {
      continue;
    }

    // Refresh Graphics
    layer.removeAll();
    const allGraphics = features.flatMap((f) =>
      createGraphicsForFeature(f, entry.internalLayer.incident?.name)
    );
    layer.addMany(allGraphics);
  }

  // Cleanup: Remove layers that are no longer in the map
  const currentVersionIdSet = new Set(internalLayerVersionsMap.keys());
  for (const [id, layer] of layersRef.current.entries()) {
    if (!currentVersionIdSet.has(id)) {
      map.remove(layer);
      layersRef.current.delete(id);
    }
  }
}
